/**
 * birds — the PURE rules of the Commons' birds (2026-09-11, "birds that can
 * fly around, land on trees, land on your parked ship and fly through cities,
 * towns and migrate … land on the ground to act like they are looking for
 * food or drinking water from a lake or pond … if a versona or user gets
 * close to them they fly off … a few different types").
 *
 * No three.js, no DOM: species, the per-bird state machine, steering, flock
 * slots, perch choice, the flee rule, the migration skeins and the population
 * targets per habitat. The module (AtlasFaunaModule) owns rendering, the
 * perch providers (trees / roofs / parked ships / shores) and the sounds.
 *
 * Steering after Reynolds (1999, "Steering Behaviors for Autonomous
 * Characters"): seek + arrive with a turn-rate cap, flock slots behind the
 * leader instead of full boids (a dozen birds, one decision maker), landings
 * as an approach from above that slows into the perch.
 */

export type BirdSpeciesKey = 'jay' | 'gull' | 'hawk' | 'starling';
export type BirdState = 'fly' | 'perch' | 'ground' | 'drink' | 'flee' | 'soar' | 'migrate';
export type PerchKind = 'tree' | 'roof' | 'ship' | 'ground' | 'shore' | 'air';
export type Habitat = 'forest' | 'coast' | 'city' | 'town' | 'open';

export interface BirdSpecies {
  key: BirdSpeciesKey;
  name: string;
  /** Wingspan, metres (the model is scaled to it). */
  wingspan: number;
  /** Flock size range (1 = solitary). */
  flock: [number, number];
  /** Cruise speed m/s, flap frequency Hz. */
  cruise: number;
  flapHz: number;
  /** Soars (circles high, rarely flapping). */
  soars: boolean;
  /** Forages on open ground; drinks at a shore. */
  forages: boolean;
  drinks: boolean;
  /** Perch preferences (weights). */
  perches: Partial<Record<PerchKind, number>>;
  /** Flight altitude band over the ground (m). */
  altitude: [number, number];
  /** People closer than this flush it (m). */
  fleeRadius: number;
  /** Habitat weights (population targets scale by these). */
  habitat: Record<Habitat, number>;
  /** Migrates in skeins. */
  migrates: boolean;
  /** Call sound key + how often it calls when settled (s between). */
  call: string;
  callEvery: [number, number];
}

export const BIRD_SPECIES: Record<BirdSpeciesKey, BirdSpecies> = {
  jay: {
    key: 'jay', name: 'cobalt jay', wingspan: 0.36, flock: [2, 5], cruise: 9, flapHz: 6, soars: false, forages: true, drinks: true,
    perches: { tree: 1, roof: 0.35, ship: 0.4, ground: 0.5, shore: 0.4 }, altitude: [4, 14], fleeRadius: 5,
    habitat: { forest: 1, coast: 0.2, city: 0.25, town: 0.6, open: 0.3 }, migrates: false, call: 'sfx_bird_jay', callEvery: [6, 16],
  },
  gull: {
    key: 'gull', name: 'pale gull', wingspan: 1.1, flock: [3, 8], cruise: 11, flapHz: 3, soars: true, forages: true, drinks: true,
    perches: { shore: 1, ground: 0.6, ship: 0.6, roof: 0.4, tree: 0.05 }, altitude: [6, 30], fleeRadius: 7,
    habitat: { forest: 0.05, coast: 1, city: 0.15, town: 0.3, open: 0.15 }, migrates: true, call: 'sfx_bird_gull', callEvery: [5, 14],
  },
  hawk: {
    key: 'hawk', name: 'amber hawk', wingspan: 1.3, flock: [1, 1], cruise: 12, flapHz: 2, soars: true, forages: false, drinks: false,
    perches: { tree: 1, roof: 0.3, ship: 0.2 }, altitude: [40, 120], fleeRadius: 12,
    habitat: { forest: 0.6, coast: 0.3, city: 0.1, town: 0.3, open: 0.8 }, migrates: false, call: 'sfx_bird_hawk', callEvery: [20, 45],
  },
  starling: {
    key: 'starling', name: 'sootwing starling', wingspan: 0.4, flock: [6, 14], cruise: 12, flapHz: 8, soars: false, forages: true, drinks: true,
    perches: { roof: 1, tree: 0.6, ground: 0.6, ship: 0.5, shore: 0.3 }, altitude: [8, 30], fleeRadius: 6,
    habitat: { forest: 0.2, coast: 0.3, city: 1, town: 0.8, open: 0.5 }, migrates: true, call: 'sfx_bird_starling', callEvery: [4, 12],
  },
};
export const BIRD_SPECIES_KEYS: BirdSpeciesKey[] = ['jay', 'gull', 'hawk', 'starling'];

// ── Population ──────────────────────────────────────────────────────────────
/** Birds live within this radius of the viewer; farther ones are recycled. */
export const BIRD_RANGE_M = 160;
export const BIRD_DESPAWN_M = 220;
/** Total birds near one viewer (by tier the module scales it). */
export const BIRD_MAX = 44;
/** Per-habitat totals at full population (before the species weights). */
export const HABITAT_POP: Record<Habitat, number> = { forest: 16, coast: 14, city: 22, town: 16, open: 8 };
/** Night: most birds roost (a fraction of the day count stays active). */
export const NIGHT_ACTIVE = 0.25;

/** Which habitat a spot is, from the environment's readings. */
export function habitatFor(forestDensity: number, nearWater: boolean, siteTier: 'city' | 'town' | 'hamlet' | null): Habitat {
  if (siteTier === 'city') return 'city';
  if (siteTier === 'town' || siteTier === 'hamlet') return 'town';
  if (nearWater) return 'coast';
  if (forestDensity >= 0.35) return 'forest';
  return 'open';
}

/** How many birds of each species this habitat wants (rounded, ≤ BIRD_MAX in total). */
export function populationFor(habitat: Habitat, night01: number, cap = BIRD_MAX): Record<BirdSpeciesKey, number> {
  const total = HABITAT_POP[habitat] * (1 - (1 - NIGHT_ACTIVE) * Math.max(0, Math.min(1, night01)));
  const weights = BIRD_SPECIES_KEYS.map((k) => BIRD_SPECIES[k].habitat[habitat]);
  const sum = weights.reduce((a, b) => a + b, 0) || 1;
  const out = { jay: 0, gull: 0, hawk: 0, starling: 0 } as Record<BirdSpeciesKey, number>;
  let used = 0;
  for (let i = 0; i < BIRD_SPECIES_KEYS.length; i++) {
    const k = BIRD_SPECIES_KEYS[i];
    let n = Math.round((total * weights[i]) / sum);
    if (k === 'hawk') n = Math.min(n, 2);
    n = Math.min(n, Math.max(0, cap - used));
    out[k] = n; used += n;
  }
  return out;
}

// ── The bird ────────────────────────────────────────────────────────────────
export interface Target { x: number; y: number; z: number; kind: PerchKind }
export interface Bird {
  id: number;
  species: BirdSpeciesKey;
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  yaw: number;
  state: BirdState;
  target: Target | null;
  /** Seconds left in the current state (perch/ground/drink dwell, flee burst, soar). */
  timer: number;
  /** Wing-flap phase (radians) and a hop/peck phase for ground animation. */
  flap: number;
  peck: number;
  /** Flock: leader id (own id when leading) and the slot index. */
  flock: number;
  slot: number;
  /** Seconds until the next call. */
  callIn: number;
  /** Soaring orbit centre. */
  orbitX: number; orbitZ: number; orbitR: number; orbitDir: 1 | -1;
  /** Air circuits to fly before the next perch (a bird in the sky, not only on a branch). */
  circuits?: number;
  /** Riding the sky around an airborne viewer (never lands while it is up there). */
  aloft?: boolean;
  /** Sent away past the range (recycled out of view, never in it). */
  leaving?: boolean;
}

export interface BirdRng { (): number }

export const TURN_RATE_RAD_S = 2.4;
export const CLIMB_RATE_MPS = 4;
export const FLEE_BURST_S = 2.2;
export const FLEE_SPEED_MULT = 1.6;
export const PERCH_DWELL_S: [number, number] = [8, 26];
export const GROUND_DWELL_S: [number, number] = [8, 22];
export const DRINK_DWELL_S: [number, number] = [6, 14];
export const SOAR_DWELL_S: [number, number] = [50, 120];
/** A perch is reached inside this distance (m). */
export const ARRIVE_M = 0.6;
/** Landing approach: slow from this distance out. */
export const ARRIVE_SLOW_M = 9;
/** The flock slot spacing behind and beside the leader (× wingspan). */
export const SLOT_BACK = 2.2;
export const SLOT_SIDE = 1.8;

export function randRange(rng: BirdRng, r: [number, number]): number { return r[0] + (r[1] - r[0]) * rng(); }

export function makeBird(id: number, species: BirdSpeciesKey, x: number, y: number, z: number, rng: BirdRng): Bird {
  const sp = BIRD_SPECIES[species];
  const yaw = rng() * Math.PI * 2;
  return {
    id, species, x, y, z, vx: -Math.sin(yaw) * sp.cruise, vy: 0, vz: -Math.cos(yaw) * sp.cruise, yaw,
    state: 'fly', target: null, timer: 0, flap: rng() * Math.PI * 2, peck: 0, flock: id, slot: 0,
    callIn: randRange(rng, sp.callEvery), orbitX: x, orbitZ: z, orbitR: 50, orbitDir: rng() < 0.5 ? 1 : -1,
  };
}

/** Wrap an angle to (−π, π]. */
export function wrapAngle(a: number): number { let r = a % (Math.PI * 2); if (r > Math.PI) r -= Math.PI * 2; if (r <= -Math.PI) r += Math.PI * 2; return r; }

/**
 * One steering step toward (tx, tz) at `speed` with a turn-rate cap; the
 * vertical rate is a separate climb limit toward ty. Returns the new heading
 * and velocity (the caller integrates position).
 */
export function steerToward(b: Bird, tx: number, ty: number, tz: number, speed: number, dt: number, turnRate = TURN_RATE_RAD_S, climb = CLIMB_RATE_MPS): void {
  const want = Math.atan2(-(tx - b.x), -(tz - b.z)); // the vehicle convention: forward = (−sin yaw, −cos yaw)
  const d = wrapAngle(want - b.yaw);
  const step = turnRate * dt;
  b.yaw = wrapAngle(Math.abs(d) <= step ? want : b.yaw + Math.sign(d) * step);
  b.vx = -Math.sin(b.yaw) * speed;
  b.vz = -Math.cos(b.yaw) * speed;
  const dy = ty - b.y;
  b.vy = Math.max(-climb, Math.min(climb, dy * 1.5));
}

/** Arrival speed: cruise far out, easing to a landing crawl at the perch. */
export function arriveSpeed(dist: number, cruise: number): number {
  if (dist >= ARRIVE_SLOW_M) return cruise;
  return Math.max(1.2, cruise * (0.15 + 0.85 * (dist / ARRIVE_SLOW_M)));
}

/** The flock slot for follower `slot` behind a leader at (lx, lz) heading `yaw`: a V behind and beside. */
export function flockSlot(lx: number, lz: number, yaw: number, slot: number, wingspan: number): { x: number; z: number } {
  const fx = -Math.sin(yaw), fz = -Math.cos(yaw);
  const rx = -Math.cos(yaw), rz = Math.sin(yaw);
  const row = Math.ceil(slot / 2);
  const side = slot % 2 === 1 ? 1 : -1;
  const back = row * SLOT_BACK * wingspan;
  const across = row * SLOT_SIDE * wingspan * side;
  return { x: lx - fx * back + rx * across, z: lz - fz * back + rz * across };
}

/** Whether a threat at distance d flushes a settled bird of this species. */
export function shouldFlee(species: BirdSpecies, distM: number): boolean { return distM < species.fleeRadius; }

/** Flee heading: straight away from the threat, up. */
export function fleeTarget(b: Bird, threatX: number, threatZ: number, alt: number): Target {
  let dx = b.x - threatX, dz = b.z - threatZ;
  const len = Math.hypot(dx, dz) || 1;
  dx /= len; dz /= len;
  return { x: b.x + dx * 40, y: b.y + alt, z: b.z + dz * 40, kind: 'air' };
}

/**
 * Pick the next perch kind by the species' preferences over what exists
 * (counts per kind), night pushes toward trees/roofs (a roost).
 */
export function pickPerchKind(species: BirdSpecies, available: Partial<Record<PerchKind, number>>, night01: number, rng: BirdRng): PerchKind | null {
  const kinds: PerchKind[] = ['tree', 'roof', 'ship', 'ground', 'shore'];
  let total = 0;
  const w: number[] = [];
  for (const k of kinds) {
    let weight = (species.perches[k] ?? 0) * ((available[k] ?? 0) > 0 ? 1 : 0);
    if ((k === 'ground' || k === 'shore') && !species.forages) weight = 0;
    if (k === 'shore' && !species.drinks) weight = 0;
    if (night01 > 0.5 && (k === 'ground' || k === 'shore')) weight *= 0.1; // roost at night
    w.push(weight); total += weight;
  }
  if (total <= 0) return null;
  let r = rng() * total;
  for (let i = 0; i < kinds.length; i++) { r -= w[i]; if (r <= 0) return kinds[i]; }
  return kinds[kinds.length - 1];
}

/** The state a bird enters on landing at a target kind. */
export function landedState(kind: PerchKind): BirdState {
  return kind === 'shore' ? 'drink' : kind === 'ground' ? 'ground' : 'perch';
}

/** Dwell time for a landed state. */
export function dwellFor(state: BirdState, rng: BirdRng): number {
  if (state === 'drink') return randRange(rng, DRINK_DWELL_S);
  if (state === 'ground') return randRange(rng, GROUND_DWELL_S);
  if (state === 'soar') return randRange(rng, SOAR_DWELL_S);
  return randRange(rng, PERCH_DWELL_S);
}

/**
 * A ground hop: every 1–2 s a 0.25–0.45 m step in a random heading (the
 * "looking for food" walk); `peck` runs a bob between hops. Returns the
 * step to apply (0 when between hops).
 */
export function groundHop(b: Bird, dt: number, rng: BirdRng): { dx: number; dz: number; pecking: boolean } {
  b.peck += dt;
  if (b.peck >= 1 + rng()) {
    b.peck = 0;
    const a = b.yaw + (rng() - 0.5) * 1.6;
    const s = 0.25 + rng() * 0.2;
    b.yaw = wrapAngle(a);
    return { dx: -Math.sin(a) * s, dz: -Math.cos(a) * s, pecking: false };
  }
  return { dx: 0, dz: 0, pecking: b.peck > 0.4 && b.peck < 0.8 };
}

/** Wing flap advance: flying birds flap at their rate; soaring birds hold (a slow glide); landed birds fold. */
export function flapAdvance(b: Bird, dt: number): number {
  const sp = BIRD_SPECIES[b.species];
  if (b.state === 'soar' || b.state === 'migrate' && sp.soars) return 0.6 * dt; // a slow glide sway
  if (b.state === 'fly' || b.state === 'flee' || b.state === 'migrate') {
    // Gliding descents flap less: when descending toward a target, half rate.
    const descending = b.vy < -1.5;
    return sp.flapHz * Math.PI * 2 * dt * (descending ? 0.45 : 1) * (b.state === 'flee' ? 1.4 : 1);
  }
  return 0;
}

// ── Soaring ─────────────────────────────────────────────────────────────────
/** The orbit target for a soaring bird: a point ahead on its circle, drifting up to its band. */
export function soarTarget(b: Bird, altTop: number, groundY: number): Target {
  const ang = Math.atan2(b.z - b.orbitZ, b.x - b.orbitX) + b.orbitDir * 0.35;
  return { x: b.orbitX + Math.cos(ang) * b.orbitR, y: groundY + altTop, z: b.orbitZ + Math.sin(ang) * b.orbitR, kind: 'air' };
}

// ── Migration ───────────────────────────────────────────────────────────────
/** A skein appears this often (s of game time) and crosses at this altitude. */
export const SKEIN_EVERY_S: [number, number] = [150, 320];
export const SKEIN_ALT_M: [number, number] = [110, 170];
export const SKEIN_SIZE: [number, number] = [9, 17];
export const SKEIN_SPAN_M = 1200;
export const SKEIN_SPEED = 16;
/** The migration bearing (radians, the vehicle yaw convention) by season: south in autumn, north in spring, else east-west drift. */
export function migrationYaw(season: 'spring' | 'summer' | 'autumn' | 'winter' | string, rng: BirdRng): number {
  const jitter = (rng() - 0.5) * 0.5;
  if (season === 'autumn') return wrapAngle(0 + jitter);          // heading −Z ("south")
  if (season === 'spring') return wrapAngle(Math.PI + jitter);    // heading +Z ("north")
  return wrapAngle((rng() < 0.5 ? Math.PI / 2 : -Math.PI / 2) + jitter);
}

export interface Skein { species: BirdSpeciesKey; x: number; y: number; z: number; yaw: number; count: number; t: number; life: number }

/** Plan a skein that crosses the viewer's sky from SKEIN_SPAN_M upwind of the viewer. */
export function planSkein(vx: number, vz: number, groundY: number, season: string, rng: BirdRng): Skein {
  const yaw = migrationYaw(season, rng);
  const fx = -Math.sin(yaw), fz = -Math.cos(yaw);
  const species: BirdSpeciesKey = rng() < 0.6 ? 'starling' : 'gull';
  const side = (rng() - 0.5) * 300;
  const rx = -Math.cos(yaw), rz = Math.sin(yaw);
  return {
    species, x: vx - fx * SKEIN_SPAN_M * 0.5 + rx * side, y: groundY + randRange(rng, SKEIN_ALT_M), z: vz - fz * SKEIN_SPAN_M * 0.5 + rz * side,
    yaw, count: Math.round(randRange(rng, SKEIN_SIZE)), t: 0, life: SKEIN_SPAN_M / SKEIN_SPEED,
  };
}

/** The V slots of a skein: index 0 leads, odd left, even right, each row 1.3 wingspans back and 1 across. */
export function skeinSlot(s: Skein, i: number, wingspan: number): { x: number; y: number; z: number; flap: number } {
  const fx = -Math.sin(s.yaw), fz = -Math.cos(s.yaw);
  const rx = -Math.cos(s.yaw), rz = Math.sin(s.yaw);
  const row = Math.ceil(i / 2);
  const side = i === 0 ? 0 : i % 2 === 1 ? -1 : 1;
  const back = row * 2.6 * wingspan, across = row * 2.0 * wingspan * side;
  const along = s.t * SKEIN_SPEED;
  return { x: s.x + fx * along - fx * back + rx * across, y: s.y + Math.sin(s.t * 0.3 + i) * 0.6, z: s.z + fz * along - fz * back + rz * across, flap: s.t * BIRD_SPECIES[s.species].flapHz * Math.PI * 2 + i * 0.7 };
}

/** The fraction of the pose blend toward the folded wings (perched/ground/drink = 1, flight = 0). */
export function foldedFor(state: BirdState): number {
  return state === 'perch' || state === 'ground' || state === 'drink' ? 1 : 0;
}
